import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { CreateProgressReportRequest, ProgressReport } from '@/types/progress-reports';

// Uses the shared api instance so these requests go through the same auth and
// single-flight token-refresh interceptor as the rest of the app.

/**
 * Where the client stands on this week's report.
 *
 * Computed on the server by the one shared rule (progress-report-due.ts), which
 * the coach's overdue panel and the reminder email also use. Deliberately not
 * recomputed here: a phone working out its own ISO week in SAST is a second
 * answer waiting to disagree with the first.
 */
export type ProgressReportStatus = {
    /** The Monday 00:00 SAST of the week being asked about. */
    periodStart: string;
    submittedThisWeek: boolean;
    hasEverSubmitted: boolean;
    /** Whole SAST days since the last report, or since the client joined. */
    daysSinceLastReport: number;
    /** Overdue: nothing in for this week AND the gap has run long enough. */
    isDue: boolean;
    thresholdDays: number;
};

/**
 * This week's report, the week it belongs to, and the client's standing.
 *
 * `report` is null until the client has sent one this week. The form reads
 * `report` to prefill itself; the dashboard reads `status`.
 */
export type CurrentProgressReport = {
    periodStart: string;
    report: ProgressReport | null;
    status: ProgressReportStatus;
};

export const progressReportsApi = {
    /**
     * Sends this week's report, or updates it if one is already in. The backend
     * keys on (client, week), so this is a submit and an edit through one call.
     */
    async create(data: CreateProgressReportRequest): Promise<ProgressReport> {
        const response = await api.post('/progress-reports', data);
        return response.data;
    },

    async getCurrent(): Promise<CurrentProgressReport> {
        const response = await api.get('/progress-reports/my-reports/current');
        return response.data;
    },

    async getMyReports(): Promise<ProgressReport[]> {
        const response = await api.get('/progress-reports/my-reports');
        return response.data;
    },

    async getMyReport(id: string): Promise<ProgressReport> {
        const response = await api.get(`/progress-reports/my-reports/${id}`);
        return response.data;
    },
};

/**
 * The client's own reports, for the progress view (#232).
 *
 * The endpoint scopes to the authenticated client in its query, so there is no
 * client id to pass and nothing to get wrong here.
 */
export function useMyProgressReports() {
    return useQuery({
        queryKey: ['my-progress-reports'],
        queryFn: () => progressReportsApi.getMyReports(),
    });
}

/** This week's report, for prefilling the form. */
export function useCurrentProgressReport() {
    return useQuery({
        queryKey: ['current-progress-report'],
        queryFn: () => progressReportsApi.getCurrent(),
        // Always fresh: the answers about to be edited must be the stored ones.
        staleTime: 0,
    });
}

/**
 * A 409 now means only one thing: two submissions for the same week landed in
 * the same instant and the unique index let one through. The everyday second
 * submit is an update, not a conflict, so this is genuinely rare — a double tap
 * on a slow connection — and the client's answers are recorded either way.
 */
export function isConcurrentSubmitError(error: unknown): boolean {
    return (error as { response?: { status?: number } })?.response?.status === 409;
}
